import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react'
import type { FormData } from '@/hooks/useSubmissionWizard'
import { checkSubmissionEligibility } from '@/lib/api'

interface EmailStepProps {
  formData: FormData
  updateField: <K extends keyof FormData>(field: K, value: FormData[K]) => void
  onNext: () => void
  onBack: () => void
  canGoBack?: boolean
  // Draft functionality
  loadDraftForEmail?: (email: string) => Promise<boolean>
  clearDraft?: () => void
  isDraftLoading?: boolean
}

interface RejectionInfo {
  previouslyRejected: boolean
  rejectionReason?: string
}

const VALID_DOMAINS = ['@scsstudent.org', '@sierracanyonschool.org']
const ALLOWED_EMAILS = ['garrett@garrettjohnlaw.com', 'ddinstman@sierracanyonschool.org']

function isValidSchoolEmail(email: string): boolean {
  const normalizedEmail = email.trim().toLowerCase()

  // Check if email is in allowed emails list (e.g., for testing)
  if (ALLOWED_EMAILS.includes(normalizedEmail)) {
    return true
  }

  // Check if domain is in allowed domains
  return VALID_DOMAINS.some((domain) => normalizedEmail.endsWith(domain))
}

export function EmailStep({
  formData,
  updateField,
  onNext,
  onBack,
  canGoBack = true,
  loadDraftForEmail,
  isDraftLoading = false,
}: EmailStepProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rejectionInfo, setRejectionInfo] = useState<RejectionInfo | null>(null)

  const handleBack = () => {
    if (canGoBack) {
      onBack()
    } else {
      navigate('/')
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  const validate = (): boolean => {
    if (!formData.email.trim()) {
      setError('Please enter your email')
      return false
    }
    if (!isValidSchoolEmail(formData.email)) {
      setError('Please use your Sierra Canyon student email (@scsstudent.org)')
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    setError(null)
    setRejectionInfo(null)

    const normalizedEmail = formData.email.trim().toLowerCase()
    const result = await checkSubmissionEligibility(normalizedEmail)

    if (result.error) {
      setError(result.error)
      setIsSubmitting(false)
      return
    }

    // Check if previously rejected - show info but allow to proceed
    if (result.data?.previouslyRejected) {
      setRejectionInfo({
        previouslyRejected: true,
        rejectionReason: result.data.rejectionReason,
      })
      setIsSubmitting(false)
      return
    }

    // Check if draft exists - automatically continue from draft
    if (result.data?.hasDraft && loadDraftForEmail) {
      updateField('email', normalizedEmail)
      await loadDraftForEmail(normalizedEmail)
      // loadDraftForEmail navigates to the saved step
      return
    }

    // Eligible with no draft - proceed to next step
    updateField('email', normalizedEmail)
    onNext()
  }

  const handleProceedAfterRejection = () => {
    // User acknowledged the rejection, proceed to next step
    updateField('email', formData.email.trim().toLowerCase())
    onNext()
  }

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (rejectionInfo) {
        // If showing rejection info, Enter proceeds
        handleProceedAfterRejection()
        return
      }
      if (validate() && !isSubmitting) {
        setIsSubmitting(true)
        setError(null)
        setRejectionInfo(null)

        const normalizedEmail = formData.email.trim().toLowerCase()
        const result = await checkSubmissionEligibility(normalizedEmail)

        if (result.error) {
          setError(result.error)
          setIsSubmitting(false)
          return
        }

        if (result.data?.previouslyRejected) {
          setRejectionInfo({
            previouslyRejected: true,
            rejectionReason: result.data.rejectionReason,
          })
          setIsSubmitting(false)
          return
        }

        // Auto-load draft if exists
        if (result.data?.hasDraft && loadDraftForEmail) {
          updateField('email', normalizedEmail)
          await loadDraftForEmail(normalizedEmail)
          return
        }

        updateField('email', normalizedEmail)
        onNext()
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-xl space-y-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
        <h2 className="text-foreground font-display text-3xl font-bold md:text-4xl lg:text-5xl">
          What's your school email?
        </h2>
        <p className="text-foreground-muted text-lg">We'll use this to verify you're a Sierra Canyon student</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Input
          ref={inputRef}
          type="email"
          placeholder="you@scsstudent.org"
          value={formData.email}
          onChange={(e) => {
            setError(null)
            setRejectionInfo(null)
            updateField('email', e.target.value)
          }}
          onKeyDown={handleKeyDown}
          error={error ?? undefined}
          hint="Use your @scsstudent.org email"
          className="text-xl"
          disabled={!!rejectionInfo}
        />
      </motion.div>

      {rejectionInfo && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <div className="space-y-1">
              <p className="font-medium text-amber-800">Your previous submission was not approved</p>
              {rejectionInfo.rejectionReason && (
                <p className="text-sm text-amber-700">Reason: {rejectionInfo.rejectionReason}</p>
              )}
              <p className="text-sm text-amber-600">You can submit a new photo that addresses the feedback above.</p>
            </div>
          </div>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-4"
      >
        <Button type="button" variant="ghost" size="lg" onClick={handleBack} disabled={isSubmitting || isDraftLoading}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {rejectionInfo ? (
          <Button
            type="button"
            size="lg"
            onClick={handleProceedAfterRejection}
            className="flex-1 md:min-w-[200px] md:flex-none"
          >
            Submit New Photo
            <ArrowRight className="h-5 w-5" />
          </Button>
        ) : (
          <Button
            type="submit"
            size="lg"
            disabled={!formData.email.trim() || isSubmitting}
            isLoading={isSubmitting || isDraftLoading}
            className="flex-1 md:min-w-[200px] md:flex-none"
          >
            Continue
            <ArrowRight className="h-5 w-5" />
          </Button>
        )}
        {<span className="text-foreground-subtle hidden text-sm md:block">or press Enter</span>}
      </motion.div>
    </form>
  )
}
